// Central source-of-truth for detail-view DISPLAY defaults.
//
// These values used to be hard-coded inside components/detail/fileUtils.js and
// components/detail/FieldValue.jsx. They now live here, are seeded with the same
// built-in defaults, and are overridable from the admin page
// (Admin → Detail Groups → Display Defaults), which persists them via
// GET/POST /admin/detail-defaults.
//
// This is a plain module (not a React component / context) so fileUtils.js — a
// non-component helper — can read it synchronously. Consumers read getDefaults()
// at render time; loadDetailDefaults() fetches the saved values once and updates
// the store (falling back to the built-ins on any failure).

import { getDetailDefaults as fetchDetailDefaults } from './adminApi';

// Built-in defaults — exactly the previous hard-coded behaviour. S3 base still
// honours VITE_S3_BASE_URL as the env-level seed; the admin value overrides it.
export const BUILT_IN_DEFAULTS = Object.freeze({
  s3BaseUrl: (
    import.meta.env?.VITE_S3_BASE_URL ||
    'https://zinnext-devlopment-ap-south-1.s3.ap-south-1.amazonaws.com'
  ).replace(/\/+$/, ''),
  // A pure CALENDAR date — a date of birth, an expiry. No time, and no zone
  // suffix: stamping a clock on a birthday claims precision it does not have.
  dateFormat: 'DD MMM YYYY',
  // An INSTANT — created/updated stamps, interview slots. Carries the time and,
  // unless switched off below, the zone it is being shown in. Without the zone
  // a timestamp is ambiguous the moment two people in different countries read
  // it.
  dateTimeFormat: 'MMM DD, YYYY | hh:mm A',
  showTimeZoneLabel: true,
  // The tenant's clock. EMPTY means "use the viewer's browser zone", which is
  // exactly the behaviour before this setting existed — so nothing changes for
  // a tenant that never sets it. See services/timezone.js.
  timeZone: '',
  // Short labels for zones ("Asia/Kolkata" → "IST"). Abbreviations are NOT
  // valid IANA identifiers and are dangerously ambiguous as inputs, so they
  // exist only as display labels here.
  timeZoneAliases: {},
  // Which per-region rule profile form groups apply ("US"/"UK"/"IND"/'' for
  // the default). Carries no logic — it is a KEY into group.regionRules, which
  // is what lets a new market be added as config rather than as code.
  region: '',
  booleanTrueLabel: 'Yes',
  booleanFalseLabel: 'No',
  emptyPlaceholder: '-',
  // Per-render-type empty texts. The single `emptyPlaceholder` above could only
  // ever say one thing ("-"), which cannot distinguish "no work experience" from
  // "no document" and reads as a rendering fault rather than as information.
  // Built-in per-type defaults live in components/detail/emptyText.js; anything
  // set here overrides them, and `default` covers every unlisted type.
  emptyTexts: {},
  separator: ' - ',
  documentNameOrder: ['name', 'documentName', 'uploadName', 'uploadedFileName', 'uniqueName'],
  // Common phone-number format — one place, applied everywhere (forms + detail),
  // exactly like `dateFormat`. Groups of digit counts separated by a literal
  // separator: "3-3-4" → 999-878-3413 (total 10 digits). Change it here (or in
  // Admin → Display Defaults) and every phone field re-formats to match.
  phoneFormat: '3-3-4',
  // LAST-RESORT country code, used only when the record itself carries none
  // (see components/detail/phoneDisplay.js). Deliberately EMPTY: a tenant that
  // wants every unqualified number stamped "+1" sets it in Admin → Display
  // Defaults, but nothing invents a country for a record that never stated one
  // — that is exactly how every candidate ended up displayed as "+1".
  phoneCountryCode: '',
});

let current = { ...BUILT_IN_DEFAULTS };
let loadPromise = null;

// getDefaults returns the live defaults (built-ins until loadDetailDefaults runs).
export function getDefaults() {
  return current;
}

// sanitize keeps only the keys that carry a real value, so a partial/empty saved
// config never blanks out a built-in.
function sanitize(d) {
  if (!d || typeof d !== 'object') return {};
  const out = {};
  if (d.s3BaseUrl) out.s3BaseUrl = String(d.s3BaseUrl).replace(/\/+$/, '');
  if (d.dateFormat) out.dateFormat = d.dateFormat;
  if (d.dateTimeFormat) out.dateTimeFormat = d.dateTimeFormat;
  // A saved `false` is a real setting ("never print the zone"), so the key is
  // honoured whenever it is present as a boolean rather than only when truthy.
  if (typeof d.showTimeZoneLabel === 'boolean') out.showTimeZoneLabel = d.showTimeZoneLabel;
  // '' is meaningful (fall back to the browser zone), so any string is honoured.
  if (typeof d.timeZone === 'string') out.timeZone = d.timeZone.trim();
  // '' is meaningful: "use each group's own defaults, no region profile".
  if (typeof d.region === 'string') out.region = d.region.trim();
  if (d.timeZoneAliases && typeof d.timeZoneAliases === 'object' && !Array.isArray(d.timeZoneAliases)) {
    out.timeZoneAliases = { ...current.timeZoneAliases, ...d.timeZoneAliases };
  }
  if (d.booleanTrueLabel) out.booleanTrueLabel = d.booleanTrueLabel;
  if (d.booleanFalseLabel) out.booleanFalseLabel = d.booleanFalseLabel;
  if (d.emptyPlaceholder) out.emptyPlaceholder = d.emptyPlaceholder;
  // Objects merge key-wise rather than replacing wholesale, so configuring one
  // render type does not blank out the others.
  if (d.emptyTexts && typeof d.emptyTexts === 'object' && !Array.isArray(d.emptyTexts)) {
    const texts = {};
    Object.entries(d.emptyTexts).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim() !== '') texts[key] = value.trim();
    });
    if (Object.keys(texts).length) out.emptyTexts = { ...current.emptyTexts, ...texts };
  }
  if (typeof d.separator === 'string' && d.separator !== '') out.separator = d.separator;
  if (typeof d.phoneFormat === 'string' && d.phoneFormat.trim() !== '') out.phoneFormat = d.phoneFormat.trim();
  // Unlike the others, an EMPTY country code is a meaningful setting ("stamp
  // nothing on a record that stated no country"), so '' is honoured instead of
  // being treated as "unset, keep the built-in".
  if (typeof d.phoneCountryCode === 'string') out.phoneCountryCode = d.phoneCountryCode.trim();
  // A saved empty string is meaningful here ("show no code at all"), so unlike
  // the others this key is accepted whenever it is present as a string.
  if (typeof d.phoneCountryCode === 'string') out.phoneCountryCode = d.phoneCountryCode.trim();
  if (Array.isArray(d.documentNameOrder) && d.documentNameOrder.length) out.documentNameOrder = d.documentNameOrder;
  return out;
}

// ── Phone number formatting (common, config-driven) ──────────────────────────
// The format string is groups of digit counts joined by a literal separator,
// e.g. "3-3-4" → [3,3,4] joined by "-". parsePhoneFormat returns { groups, sep,
// total } so both the formatter and the length validation share one definition.
export function parsePhoneFormat(format = getPhoneFormat()) {
  const groups = (String(format).match(/\d+/g) ?? ['3', '3', '4']).map(Number).filter((n) => n > 0);
  const sep = (String(format).match(/\D+/)?.[0]) ?? '-';
  const safeGroups = groups.length ? groups : [3, 3, 4];
  return { groups: safeGroups, sep, total: safeGroups.reduce((a, b) => a + b, 0) };
}

// Live phone format from the same singleton the detail defaults use.
export function getPhoneFormat() {
  return current.phoneFormat || BUILT_IN_DEFAULTS.phoneFormat;
}

// The tenant-wide fallback country code — see BUILT_IN_DEFAULTS for why it is
// empty by default. Consumed by components/detail/phoneDisplay.js as the LAST
// resort, after the record's own code.
export function getPhoneCountryCode() {
  return current.phoneCountryCode ?? BUILT_IN_DEFAULTS.phoneCountryCode;
}

// Strips everything but digits, capped at the configured total (default 10).
export function phoneDigits(value, format = getPhoneFormat()) {
  const { total } = parsePhoneFormat(format);
  return String(value ?? '').replace(/\D/g, '').slice(0, total);
}

// formatPhone turns any input into the configured mask as the user types:
// "9998783413" → "999-878-3413". Partial input formats progressively
// ("99987" → "999-87"); non-digits are ignored.
export function formatPhone(value, format = getPhoneFormat()) {
  const { groups, sep } = parsePhoneFormat(format);
  const digits = phoneDigits(value, format);
  if (!digits) return '';
  const chunks = [];
  let i = 0;
  for (const size of groups) {
    if (i >= digits.length) break;
    chunks.push(digits.slice(i, i + size));
    i += size;
  }
  return chunks.join(sep);
}

// applyDefaults merges a (partial) config over the current store immediately —
// used by the admin page right after a successful save so the change is live
// without a reload.
export function applyDefaults(partial) {
  current = { ...current, ...sanitize(partial) };
  return current;
}

// loadDetailDefaults fetches the saved defaults once (cached). Safe to call from
// anywhere — failures silently keep the built-ins. Pass force=true to refetch.
export function loadDetailDefaults(force = false) {
  if (loadPromise && !force) return loadPromise;
  loadPromise = fetchDetailDefaults()
    .then((d) => applyDefaults(d))
    .catch(() => current);
  return loadPromise;
}
